'use client';

/**
 * ShareSheet — the shared UI for sharing something via a short URL.
 *
 * Flow: mount → "Generate link" → caller-provided `onGenerate()` builds
 * + encrypts + uploads the bundle → show URL + Copy / Native Share
 * buttons. Nothing is persisted on the sender's device.
 *
 * Extracted in the 1394–1411 review sweep — ShareProjectSheet and
 * ShareYarnSheet were near-identical 225-line files. They are now thin
 * wrappers that supply the four things that actually differ: the modal
 * title, the explain copy, the native-share name, and the `onGenerate`
 * implementation (`.kproj` vs `.kyarn` bundle).
 *
 * See `SHARING-AND-MONETIZATION-PLAN.md`.
 */

import { useState, useEffect } from 'react';
import { Check, Copy, Link as LinkIcon, Loader2, Send, AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import { ShareError } from '@/lib/shareLink';
import { useI18n } from '@/lib/i18n';
import { ZineButton } from './ui/ZineButton';
import { MonoLabel } from './ui/MonoLabel';
import { ItalicBody } from './ui/ItalicBody';

export type ShareResult = { url: string; expiresInSeconds: number };

interface ShareSheetProps {
  open: boolean;
  onClose: () => void;
  /** Modal title, e.g. t.shareProject / t.shareYarn. */
  title: string;
  /** Explanatory paragraph shown in the idle state. */
  explain: string;
  /** Display name used in the native-share title/text. */
  shareName: string;
  /** Builds, encrypts and uploads the bundle; returns the share URL. */
  onGenerate: () => Promise<ShareResult>;
}

export default function ShareSheet({ open, onClose, title, explain, shareName, onGenerate }: ShareSheetProps) {
  const { t } = useI18n();
  const [state, setState] = useState<'idle' | 'building' | 'ready' | 'error'>('idle');
  const [url, setUrl] = useState<string>('');
  const [expiresDays, setExpiresDays] = useState<number>(7);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    if (!open) {
      // Reset when closed so re-opening starts fresh.
      setState('idle');
      setUrl('');
      setCopied(false);
      setError('');
    }
  }, [open]);

  const generate = async () => {
    setState('building');
    setError('');
    try {
      const result = await onGenerate();
      setUrl(result.url);
      setExpiresDays(Math.round(result.expiresInSeconds / 86400));
      setState('ready');
    } catch (e) {
      setState('error');
      if (e instanceof ShareError) setError(e.message);
      else setError((e as Error).message || 'Something went wrong generating the share link.');
    }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no-op; the input is still selectable
    }
  };

  const share = async () => {
    if (!url || !canNativeShare) return;
    try {
      await navigator.share({
        title: `${shareName} — Knitter's Library`,
        text: `I shared "${shareName}" with you on Knitter's Library.`,
        url,
      });
    } catch {
      // User cancelled — no-op.
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        {state === 'idle' && (
          <div className="flex flex-col" style={{ gap: 32 }}>
            <p style={{ color: 'var(--ink)', fontFamily: 'var(--font-geist-sans), system-ui, sans-serif', fontSize: 13, lineHeight: 1.5, margin: 0 }}>
              {explain}
            </p>
            <div className="flex">
              <ZineButton variant="solid" onClick={generate}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <LinkIcon size={16} />
                  {t.shareGenerateLink}
                </span>
              </ZineButton>
            </div>
          </div>
        )}

        {state === 'building' && (
          <div className="py-6 flex flex-col items-center gap-3">
            <Loader2 size={24} style={{ color: 'var(--ink)' }} className="animate-spin" />
            <p style={{ color: 'var(--ink-2)', fontFamily: 'var(--font-geist-sans), system-ui, sans-serif', fontSize: 13, margin: 0 }}>
              {t.shareGenerating}
            </p>
          </div>
        )}

        {state === 'ready' && (
          <>
            <div>
              <MonoLabel>{t.shareLink}</MonoLabel>
              <div className="flex items-stretch" style={{ gap: 8 }}>
                <input
                  type="text"
                  readOnly
                  value={url}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '8px 10px',
                    background: 'var(--paper-1)',
                    border: '1px solid var(--ink)',
                    borderRadius: 0,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: 'var(--ink)',
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={copy}
                  aria-label={t.copyLink}
                  style={{
                    padding: '0 12px',
                    background: 'var(--paper-2)',
                    border: '1px solid var(--ink)',
                    color: copied ? 'var(--in-use)' : 'var(--ink)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  {copied ? <><Check size={12} /> {t.copiedLabel}</> : <><Copy size={12} /> {t.copy}</>}
                </button>
              </div>
            </div>

            {canNativeShare && (
              <div className="flex">
                <ZineButton variant="solid" onClick={share}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Send size={16} />
                    {t.shareViaApp}
                  </span>
                </ZineButton>
              </div>
            )}

            <div
              style={{
                padding: '10px 12px',
                background: 'var(--paper-2)',
                border: '1px dashed var(--ink)',
              }}
            >
              <ItalicBody size="sm">
                {(t.shareExpiresNote ?? 'This link works for {days} days, then disappears.').replace('{days}', String(expiresDays))}
              </ItalicBody>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div
              style={{
                padding: '10px 12px',
                border: '1px solid var(--over)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <AlertTriangle size={16} style={{ color: 'var(--over)', flexShrink: 0, marginTop: 2 }} />
              <p
                style={{
                  color: 'var(--over)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                {error}
              </p>
            </div>
            <div className="flex">
              <ZineButton variant="outlined" onClick={() => setState('idle')}>
                {t.tryAgain}
              </ZineButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
